import {createContext, FC, useContext, useEffect, useMemo, useState} from 'react';
import {WithChildren} from '../helpers';

export interface PageLink {
	title: string;
	path: string;
	isActive: boolean;
	isSeparator?: boolean;
}

export interface PageDataContextModel {
	pageTitle?: string;
	setPageTitle: (_title: string) => void;
	isSmallPageTitle?: boolean;
	setIsSmallPageTitle: (val: boolean) => void;
	pageBreadcrumbs?: Array<PageLink>;
	setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void;
	showTitleInBreadcrumb?: boolean;
	setShowTitleInBreadcrumb: (_value: boolean) => void;
}

const PageDataContext = createContext<PageDataContextModel>({
	setPageTitle: (_title: string) => {
		console.log(_title);
	},
	setIsSmallPageTitle: (val: boolean) => {
		console.log(val);
	},
	setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {
		console.log(_breadcrumbs);
	},
	showTitleInBreadcrumb: true,
	setShowTitleInBreadcrumb: (_value: boolean) => {
		console.log(_value);
	},
});

const PageDataProvider: FC<WithChildren> = ({children}) => {
	const [pageTitle, setPageTitle] = useState<string>('');
	const [isSmallPageTitle, setIsSmallPageTitle] = useState<boolean>(false);
	const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Array<PageLink>>([]);
	const [showTitleInBreadcrumb, setShowTitleInBreadcrumb] = useState<boolean>(true);
	const value: PageDataContextModel = useMemo(() => {
		return {
			pageTitle,
			setPageTitle,
			isSmallPageTitle,
			setIsSmallPageTitle,
			pageBreadcrumbs,
			setPageBreadcrumbs,
			showTitleInBreadcrumb,
			setShowTitleInBreadcrumb,
		};
	}, [pageTitle, isSmallPageTitle, setIsSmallPageTitle, pageBreadcrumbs, showTitleInBreadcrumb]);
	return <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>;
};

function usePageData() {
	return useContext(PageDataContext);
}

type Props = {
	small?: boolean;
	breadcrumbs?: Array<PageLink>;
	showTitleInBreadcrumb?: boolean;
};

const PageTitle: FC<Props & WithChildren> = ({children, small = false, breadcrumbs, showTitleInBreadcrumb = true}) => {
	const {setPageTitle, setIsSmallPageTitle, setPageBreadcrumbs, setShowTitleInBreadcrumb} = usePageData();
	useEffect(() => {
		if (children) {
			setPageTitle(children.toString());
			if (small) {
				setIsSmallPageTitle(small);
			}
		}
		return () => {
			setPageTitle('');
			setIsSmallPageTitle(false);
		};
	}, [children, setPageTitle, small, setIsSmallPageTitle]);

	useEffect(() => {
		if (breadcrumbs) {
			setPageBreadcrumbs(breadcrumbs);
		}
		return () => {
			setPageBreadcrumbs([]);
		};
	}, [breadcrumbs, setPageBreadcrumbs]);

	useEffect(() => {
		if (showTitleInBreadcrumb) {
			setShowTitleInBreadcrumb(true);
		}
		return () => {
			setShowTitleInBreadcrumb(false);
		};
	}, [showTitleInBreadcrumb, setShowTitleInBreadcrumb]);

	return null;
};

export {PageTitle, PageDataProvider, usePageData};
